import { formatPriceCurrency, getCurrency, showError } from 'utils/utils';
import { faCheck, faHotel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  checkMultiPaymentStatus,
  checkPaymentStatus,
  fakeApproveCreditGuard,
  fakeApproveFinalStep,
  newPayment,
  refreshPayment,
} from 'services/payment';
import { faLoader, faSpinnerThird } from '@fortawesome/sharp-duotone-solid-svg-icons';
import Button from 'components/ui/Button/Button';
import withErrorBoundary from './withErrorBoundary';

const MultiPaymentPage: FC = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const currentPaymentIndex = useRef(0);
  const [iframeUrl, setIframeUrl] = useState('');
  const navigate = useNavigate();
  const tranId = useRef('');
  const ordId = useRef(0);
  const dataRef = useRef<any>(null);
  const [dataState, setDataState] = useState<any>(null);
  const [isFinalLoading, setIsFinalLoading] = useState(false);

  const timeout = useRef<any>();
  const triesCount = useRef(60);

  useEffect(() => {
    return () => {
      clearTimeout(timeout.current);
    };
  }, []);

  useEffect(() => {
    const bookPayments = sessionStorage.getItem('bookPaymentsHotels');
    if (bookPayments) {
      const parsed = JSON.parse(bookPayments);
      if (parsed) {
        let multiPayments = parsed.multiPaymentSettings;
        const isSomeStatuses = parsed.multiPaymentSettings.some((payment: any) => payment.status);
        if (!isSomeStatuses) {
          multiPayments[currentPaymentIndex.current].status = 'ConfirmationWaitingFromCreditGuard';
        } else {
          currentPaymentIndex.current = parsed.multiPaymentSettings.findIndex(
            (payment: any) =>
              payment.status === 'ConfirmationWaitingFromCreditGuard' ||
              payment.status === 'FinalStage'
          );
        }
        dataRef.current = parsed;

        tranId.current = parsed.transactionId;
        if (parsed.data) {
          ordId.current = parsed.data.orderId;
        }
        setDataState(parsed);
        setIframeUrl(parsed.paymentUrl);
        check();
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const check = async () => {
    triesCount.current -= 1;
    if (triesCount.current <= 0) {
      showError(t('something_went_wrong'));
      return;
    }

    const responseCheck = await checkMultiPaymentStatus(tranId.current, ordId.current);

    if (responseCheck?.status?.state === 'ConfirmationWaitingFromCreditGuard') {
      timeout.current = setTimeout(() => check(), 10000);
    } else if (responseCheck?.status?.state === 'NeedToRefresh') {
      showError(responseCheck?.status?.errorMessageToShow || t('something_went_wrong'));
      const responseRefresh = await refreshPayment(tranId.current, ordId.current);
      if (responseRefresh?.newPayment) {
        dataRef.current.paymentUrl = responseRefresh.newPayment.paymentUrl;
        dataRef.current.transactionId = responseRefresh.newPayment.transactionId;
        sessionStorage.setItem('bookPaymentsHotels', JSON.stringify(dataRef.current));

        tranId.current = responseRefresh.newPayment.transactionId;
        setIframeUrl(responseRefresh.newPayment.paymentUrl);

        timeout.current = setTimeout(() => {
          check();
        }, 10000);
      }
    } else if (responseCheck?.status?.state === 'SuccessJ5') {
      triesCount.current = 60;
      let newMultiPayments = [...dataRef.current.multiPaymentSettings];
      newMultiPayments[currentPaymentIndex.current].status = 'SuccessJ5';
      newMultiPayments[currentPaymentIndex.current].transactionId = tranId.current;
      currentPaymentIndex.current += 1;
      if (currentPaymentIndex.current < dataRef.current.multiPaymentSettings.length) {
        newMultiPayments[currentPaymentIndex.current].status = 'ConfirmationWaitingFromCreditGuard';

        const newPaymentResponse = await newPayment(ordId.current);
        if (newPaymentResponse?.newPayment) {
          const newIframeUrl = newPaymentResponse.newPayment.paymentUrl;
          dataRef.current.paymentUrl = newIframeUrl;
          dataRef.current.multiPaymentSettings = newMultiPayments;
          dataRef.current.transactionId = newPaymentResponse.newPayment.transactionId;
          sessionStorage.setItem('bookPaymentsHotels', JSON.stringify(dataRef.current));

          tranId.current = newPaymentResponse.newPayment.transactionId;
          setIframeUrl(newIframeUrl);

          timeout.current = setTimeout(() => {
            check();
          }, 10000);
        }
      }
    } else if (responseCheck?.status?.state === 'FinalStage') {
      setIsFinalLoading(true);
      triesCount.current = 100;
      const lastIndex = dataRef.current.multiPaymentSettings.length - 1;
      dataRef.current.multiPaymentSettings[lastIndex].status = 'FinalStage';
      sessionStorage.setItem('bookPaymentsHotels', JSON.stringify(dataRef.current));
      checkFinal();
    }
  };

  const checkFinal = async () => {
    triesCount.current -= 1;
    const responseCheck = await checkPaymentStatus(undefined, ordId.current);

    if (responseCheck?.bookingModel?.statusGoToXX) {
      sessionStorage.removeItem('bookPaymentHotels');
      sessionStorage.setItem(
        'bookStatusXX',
        JSON.stringify({
          orderId: ordId.current,
          transactionId: tranId.current,
        })
      );

      navigate('/status-xx');
    } else if (responseCheck?.orderHasBeenPaid) {
      sessionStorage.removeItem('bookPaymentHotels');
      sessionStorage.setItem(
        'bookSuccessHotels',
        JSON.stringify({
          book: dataRef.current.data,
          paxes: dataRef.current.data.paxes,
          orderId: ordId.current,
          transactionId: tranId.current,
          couponAmount: dataRef.current.data?.couponAmount,
        })
      );

      navigate('/hotel/success');
    } else if (responseCheck?.hasErrors) {
      sessionStorage.setItem('errMessage', responseCheck?.errorMessage);
      sessionStorage.setItem(
        'bookFail',
        JSON.stringify({
          orderId: ordId.current,
          transactionId: tranId.current,
        })
      );
      navigate('/fail');
    } else if (triesCount.current > 0) {
      timeout.current = setTimeout(() => checkFinal(), 5000);
    }
  };

  return (
    <Fragment>
      <div className="details-wrapper payment-wrapper-multi">
        <div className="default-shadow details-block w100p">
          <div className="title text-md title-header-grey">
            <FontAwesomeIcon icon={faHotel} className="mr-5" size={'sm'} />
            {t('hotel.multi_payment')}
          </div>
          <Button
            className="btn btn-primary mr-5"
            title="Fake Success"
            onClick={() => fakeApproveCreditGuard(ordId.current, tranId.current, false)}
          />
          <Button
            title="Fake fail"
            className="btn btn-danger mr-5"
            onClick={() => fakeApproveCreditGuard(ordId.current, tranId.current, true)}
          />
          <Button
            title="Fake Final Success"
            className="btn btn-success mr-5"
            onClick={() => fakeApproveFinalStep(ordId.current, false)}
          />
          <Button
            title="Fake Final Fail"
            className="btn btn-danger mr-5"
            onClick={() => fakeApproveFinalStep(ordId.current, true)}
          />
          <div className="table-default mb-10 mt-10">
            <table className="table-bordered-lines table-mobile">
              <thead>
                <tr>
                  <th>#</th>
                  <th>{t('passenger.number_of_payments')}</th>
                  <th>{t('mo.amount')}</th>
                  <th>{t('mo.status_full')}</th>
                </tr>
              </thead>
              <tbody>
                {dataState?.multiPaymentSettings?.map((payment: any, index: number) => (
                  <tr key={payment.id || index}>
                    <td>{index + 1}</td>
                    <td>
                      {payment.listOfRegularPayments?.find(
                        (p: any) => p.value === payment.numberOfRegularPayments + ''
                      )?.text || payment.numberOfRegularPayments}
                    </td>
                    <td>{formatPriceCurrency(payment.fullPriceInShekel, getCurrency('ILS'))}</td>
                    <td>
                      {payment.status === 'ConfirmationWaitingFromCreditGuard' && (
                        <span className="text-contrast">
                          <FontAwesomeIcon
                            icon={faLoader as any}
                            className="bp-spin text-contrast"
                          />
                        </span>
                      )}
                      {(payment.status === 'SuccessJ5' || payment.status === 'FinalStage') && (
                        <span className="text-success">
                          <FontAwesomeIcon icon={faCheck} />
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div className="w100p default-shadow">
          {iframeUrl && (
            <iframe
              src={iframeUrl}
              title="pay"
              width={470}
              height={577}
              style={{ margin: '20px 0' }}
            />
          )}
        </div>
      </div>
      {isFinalLoading && (
        <div
          className="loader-main default-shadow loader-main-nocontent"
          style={{ background: 'var(--color-bg-light)', padding: '30px 0' }}
        >
          <div className="loader-main-content default-shadow">
            <div className="text-lh">
              <div className="title">{t('passenger.please_wait')}</div>
              <div className="mb-5">{t('passenger.your_payment_is_being_processed')}</div>
              <FontAwesomeIcon
                icon={faSpinnerThird as any}
                className="bp-spin text-contrast mt-10"
                size="xl"
              />
            </div>
          </div>
        </div>
      )}
    </Fragment>
  );
};

export default withErrorBoundary(MultiPaymentPage, 'MultiPaymentPage');
